import type { Character } from '@/schemas/character';
import type { Follower, FollowerModifiers } from '@/schemas/follower';
import { computeFollowerStats } from '@/services/followerStats';
import { SheetSection } from './SheetSection';

// organism — DWR FullSheet seguaci. Sezione condizionale: mostrata solo se
// il PG ha follower (mosse-source di Follower: Compagno Animale Ranger,
// Famiglio Mago, Banda di Guerra Barbaro, etc.).
//
// **PR feature/hub-mobile-complete (FI-61)**: regressione fix — il display
// dei Follower era nel `<ReviewPanel>` rimosso in PR #91. Ora ha sezione
// dedicata nella scheda Hub permanente.
//
// **FI-63 (Sprint C)**: la card usa ora `computeFollowerStats(follower, character)`
// per derivare PF/Danno/Carico/Armatura applicando i trait `effects`
// (`damageDieOverride`, `hpDelta`, `loadDelta`, `armorDelta`). Pre-fix la
// card leggeva `follower.stats.damageDie` base e ignorava i trait — bug
// confermato su Ranger+Compagno Animale con tratto `feroce` (d6 mostrato
// invece di d8 atteso). Aggiunta anche la riga Armatura (oggi assente).
//
// Pattern coerente con altre `<SheetSection>` (border-bottom ink h2, body
// flex-column). Sub-component `<FollowerCard>` page-specific: nome + tipo +
// HP + armatura + danno + carico + abilità.

interface FullSheetFollowersProps {
  character: Character;
}

export function FullSheetFollowers({ character }: FullSheetFollowersProps) {
  if (character.followers.length === 0) return null;
  return (
    <SheetSection title={character.followers.length === 1 ? 'Seguace' : 'Seguaci'}>
      <div className="flex flex-col gap-3">
        {character.followers.map((follower) => (
          <FollowerCard key={follower.id} follower={follower} character={character} />
        ))}
      </div>
    </SheetSection>
  );
}

interface FollowerCardProps {
  follower: Follower;
  character: Character;
}

// page-specific, not reusable: card del Follower per la scheda Hub. Layout
// compatto: nome+tipo header, stats inline (HP+danno+carico+armatura+livello),
// trait/skill chips se presenti.
function FollowerCard({ follower, character }: FollowerCardProps) {
  const typeLabel = formatFollowerType(follower.type);
  const derived = computeFollowerStats(follower, character);
  return (
    <article className="border-rule bg-paper flex flex-col gap-2 border p-3">
      <header className="flex flex-wrap items-baseline gap-2">
        <strong className="font-display text-[0.95rem] tracking-[0.06em] uppercase">
          {follower.name}
        </strong>
        <span className="text-ink-faint text-xs italic">
          {typeLabel}
          {follower.archetype ? ` · ${follower.archetype}` : null}
        </span>
        <span className="text-ink-soft ml-auto text-xs">L{follower.stats.level}</span>
      </header>
      <div className="grid grid-cols-2 gap-2 text-[0.85rem] sm:grid-cols-4">
        <FollowerStat label="PF" value={`${follower.currentHp}/${derived.maxHp}`} />
        <FollowerStat label="Danno" value={derived.damageDie} />
        <FollowerStat label="Carico" value={formatLoad(derived.load)} />
        <FollowerStat label="Armatura" value={String(derived.armor)} />
      </div>
      <FollowerModifiersGrid modifiers={follower.stats.modifiers} />
      {follower.cost ? (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[0.85rem]">
          <span className="font-display text-ink-soft text-[0.65rem] tracking-[0.08em] uppercase">
            Costo:
          </span>
          <span className="text-ink">{follower.cost.motivation}</span>
        </div>
      ) : null}
      {follower.skills.length > 0 ? (
        <div className="text-ink-soft flex flex-wrap gap-1 text-xs">
          {follower.skills.map((skill, i) => (
            <span
              key={`${skill.abilityId}-${i}`}
              className="border-rule bg-paper-bright border px-2 py-0.5"
              title={skill.isPrimary ? 'Abilità primaria' : undefined}
            >
              {skill.abilityId} <span className="text-ink-faint">R{skill.rank}</span>
              {skill.isPrimary ? ' ★' : ''}
            </span>
          ))}
        </div>
      ) : null}
      {follower.traits.length > 0 ? (
        <ul className="m-0 flex flex-col gap-0.5 p-0 text-[0.85rem]">
          {follower.traits.map((trait) => (
            <li
              key={trait.id}
              className="before:text-ink-soft relative pl-2.5 before:absolute before:left-0 before:content-['▸']"
            >
              <strong>{trait.label}</strong>
              {trait.description ? (
                <span className="text-ink-soft italic"> — {trait.description}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function FollowerStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="font-display text-ink-soft text-[0.65rem] tracking-[0.08em] uppercase">
        {label}
      </span>
      <span className="text-ink text-[0.95rem]">{value}</span>
    </div>
  );
}

/**
 * Griglia dei 6 modificatori di Caratteristica del Follower (FOR / DES /
 * COS / INT / SAG / CAR). I valori vengono dal template (base +
 * `modifierBonus` applicato alla stat scelta dal giocatore via
 * `selectedChoiceGroups['modificatore']`). Mostrati tutti, anche se a 0 —
 * coerente con la card del PG nella scheda.
 *
 * Feedback Francesco 2026-05-21: la card del Follower nella scheda Hub
 * mostrava già PF/Danno/Carico/Armatura ma mancavano le caratteristiche.
 */
function FollowerModifiersGrid({ modifiers }: { modifiers: FollowerModifiers }) {
  const entries: readonly { label: string; value: number }[] = [
    { label: 'FOR', value: modifiers.forza },
    { label: 'DES', value: modifiers.destrezza },
    { label: 'COS', value: modifiers.costituzione },
    { label: 'INT', value: modifiers.intelligenza },
    { label: 'SAG', value: modifiers.saggezza },
    { label: 'CAR', value: modifiers.carisma },
  ];
  return (
    <div className="grid grid-cols-3 gap-2 text-[0.85rem] sm:grid-cols-6">
      {entries.map((entry) => (
        <FollowerStat key={entry.label} label={entry.label} value={formatSignedMod(entry.value)} />
      ))}
    </div>
  );
}

function formatSignedMod(value: number): string {
  if (value > 0) return `+${value}`;
  return String(value);
}

function formatLoad(load: number | 'narrative'): string {
  if (load === 'narrative') return 'narrativo';
  return String(load);
}

function formatFollowerType(type: string): string {
  if (type === 'hireling') return 'gregario';
  if (type === 'summon') return 'evocazione';
  if (type === 'animal-companion') return 'compagno animale';
  if (type === 'undead') return 'non-morto';
  if (type === 'collective') return 'gregario collettivo';
  if (type === 'mount') return 'cavalcatura';
  return type;
}
